import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Session, User } from "@supabase/supabase-js"
import { useQuery } from "@tanstack/react-query"

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  
  // Define the profile query at the top level of the hook
  const { data: npoProfile, isLoading: isLoadingProfile, error: profileError } = useQuery({
    queryKey: ["npoProfile", session?.access_token],
    queryFn: async () => {
      if(isTokenExpired(session)) {
        return null;
      }
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/profile`, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Profile fetch error:", errorData);
        throw new Error(errorData.message || "Failed to fetch NPO profile");
      }
      const { data } = await response.json();
      return data;
    },
    enabled: !!session?.access_token,
    retry: 1
  });

  // Initial load effect
  useEffect(() => {
    const loadStoredSession = async () => {
      // Try to get session from localStorage on mount
      const storedSession = localStorage.getItem('session')
      const storedUser = localStorage.getItem('user')
      const storedProfile = localStorage.getItem('profile')

      if (!storedSession || !storedUser) {
        setIsLoading(false)
        return
      }

      try {
        const parsedSession = JSON.parse(storedSession)
        if (isTokenExpired(parsedSession)) {
          await refreshToken(parsedSession.refresh_token)
        } else {
          setSession(parsedSession)
          setUser(JSON.parse(storedUser))

          if(storedProfile) {
            setProfile(JSON.parse(storedProfile))
          }
        }
      } catch (error) {
        console.error('Error parsing stored auth data:', error)
        clearAuthData()
      } finally {
        setIsLoading(false)
      }
    }
    loadStoredSession()
  }, [isLoadingProfile])

  // Update profile when npoProfile changes
  useEffect(() => {
    if (!isLoadingProfile && npoProfile) {
      setProfile(npoProfile)
    }
  }, [isLoadingProfile, npoProfile])
  

  const isTokenExpired = (session: Session) => {
    if (!session?.expires_at) return true
    const expiresAt = session.expires_at * 1000 // Convert to milliseconds
    const now = Date.now()
    // Add 5 minute buffer to ensure token is refreshed before it actually expires
    return now >= (expiresAt - 24 * 60 * 60 * 1000)
  }

  const refreshToken = async (refreshToken: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const { data: authData } = await response.json();
      if (authData.session) {
        saveAuthData(authData.session, authData.session.user, authData.profile);
        return authData.session;
      }
      throw new Error('No session in refresh response');
    } catch (error) {
      console.error('Error refreshing token:', error);
      clearAuthData();
      redirectToLogin();
      return null;
    }
  }

  const getValidToken = async (): Promise<string | null> => {
    if (!session) return null;
    
    return session.access_token;
  }

  const clearAuthData = () => {
    localStorage.removeItem('session')
    localStorage.removeItem('user')
    localStorage.removeItem('profile')
    localStorage.removeItem('onboarding_completed')
    localStorage.removeItem('onboarding_data')
    setSession(null)
    setUser(null)
    setProfile(null)
  }

  const saveAuthData = (session: Session, user: User, profile: any) => {
    localStorage.setItem('session', JSON.stringify(session))
    localStorage.setItem('user', JSON.stringify(user))
    localStorage.setItem('profile', JSON.stringify(profile))
    setSession(session)
    setUser(user)
    setProfile(profile)
  }

  // Removed getProfile function as it's now defined at the top level
  
  const signIn = async (email: string, password: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw error;
      }

      const { data: authData } = await response.json();

      if (authData.session) {
        saveAuthData(authData.session, authData.session.user, authData.profile);
      }

      setIsLoading(false);
      return { data: authData, error: null };
    } catch (error) {
      setIsLoading(false);
      return { data: null, error };
    }
  };

  const signUp = async (email: string, password: string, metadata?: any, inviteCode?: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, ...metadata, inviteCode }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw error;
      }

      const { data: authData } = await response.json();

      if (authData.session) {
        saveAuthData(authData.session, authData.session.user, authData.profile);
      }

      return { data: authData, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/signout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw error;
      }

      clearAuthData();
      window.location.reload();
    } catch (error) {
      console.error('Sign out error:', error);
      // Still clear local data even if API call fails
      clearAuthData();
      window.location.reload();
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send reset instructions');
      }

      return { success: true };
    } catch (error) {
      console.error('Request password reset error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token: string, refreshToken: string, newPassword: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token: token, refresh_token: refreshToken, password: newPassword }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to reset password');
      }

      return { success: true };
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const redirectToLogin = () => {
    const currentPath = window.location.pathname
    const currentSearch = window.location.search
    const searchParams = new URLSearchParams()
    if (currentPath !== "/login") {
      const fullPath = currentPath + currentSearch
      searchParams.set("redirect", fullPath)
    }
    navigate({
      pathname: "/login",
      search: searchParams.toString()
    })
  }

  const redirectToUnAuthorized = () => {
    navigate("/unauthorized", { 
      state: { 
        code: 401,
        message: "You are not authorized to access this resource"
      } 
    })
  }

  const handleSessionExpiry = () => {
    clearAuthData()
    navigate('/login')
  }

  return {
    session,
    user,
    profile,
    isLoading,
    loading,
    signIn,
    signUp,
    signOut,
    requestPasswordReset,
    resetPassword,
    redirectToLogin,
    redirectToUnAuthorized,
    getValidToken,
    isTokenExpired,
    refreshToken,
    handleSessionExpiry
  }
}